/**
 * [PLP] Content Rows
 *
 * Desktop: rows are a static two-column grid; each gallery is manual (arrows).
 * Mobile: the rows become one scroll-snap carousel, so the galleries
 * auto-advance instead of exposing a second swipe surface inside the first.
 *
 * Autoplay pauses off-screen and is skipped under prefers-reduced-motion.
 */

const PLP_ROWS_MQ_MD = (window.theme && window.theme.mediaQueries && window.theme.mediaQueries.md) || '(min-width: 769px)';

const plpRowsPrefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class PlpRowsGallery {
  constructor(el) {
    this.el = el;
    this.slides = Array.from(el.querySelectorAll('.plp-rows__slide'));
    this.index = 0;
    this.timer = null;
    this.visible = true;

    if (this.slides.length < 2) return;

    this.interval = (parseFloat(el.dataset.plpInterval) || 5) * 1000;
    this.autoplayMobile = el.dataset.plpAutoplayMobile === 'true';
    this.autoplayDesktop = el.dataset.plpAutoplayDesktop === 'true';

    const prev = el.querySelector('[data-plp-gallery-prev]');
    const next = el.querySelector('[data-plp-gallery-next]');
    if (prev) prev.addEventListener('click', () => this.step(-1, true));
    if (next) next.addEventListener('click', () => this.step(1, true));

    this.go(0);
  }

  get enabled() {
    return this.slides.length > 1;
  }

  go(index) {
    if (!this.enabled) return;

    this.index = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  }

  /** @param {number} delta @param {boolean} [fromUser] restarts autoplay */
  step(delta, fromUser) {
    this.go(this.index + delta);
    if (fromUser) this.restart();
  }

  shouldAutoplay() {
    if (!this.enabled || plpRowsPrefersReducedMotion() || !this.visible) return false;
    return window.matchMedia(PLP_ROWS_MQ_MD).matches ? this.autoplayDesktop : this.autoplayMobile;
  }

  restart() {
    this.stop();
    if (!this.shouldAutoplay()) return;
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  setVisible(visible) {
    this.visible = visible;
    this.restart();
  }
}

class PlpContentRows extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    if (window.initLazyScript) {
      window.initLazyScript(this, this.init.bind(this));
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.teardown();
  }

  init() {
    this.galleries = Array.from(this.querySelectorAll('[data-plp-gallery]')).map((el) => new PlpRowsGallery(el));

    this.track = this.querySelector('[data-plp-rows-track]');
    this.dots = Array.from(this.querySelectorAll('[data-plp-rows-dot]'));
    this.rows = Array.from(this.querySelectorAll('.plp-rows__row'));

    this.initDots();
    this.observeViewport();

    this.breakpointChangeHandler = this.handleBreakpointChange.bind(this);
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
  }

  teardown() {
    if (this.galleries) this.galleries.forEach((gallery) => gallery.stop());
    if (this.viewportObserver) this.viewportObserver.disconnect();
    if (this.rowObserver) this.rowObserver.disconnect();
    if (this.breakpointChangeHandler) {
      window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }
  }

  handleBreakpointChange() {
    this.galleries.forEach((gallery) => gallery.restart());
  }

  /** Pauses every gallery while the section is outside the viewport. */
  observeViewport() {
    if (!('IntersectionObserver' in window)) {
      this.galleries.forEach((gallery) => gallery.setVisible(true));
      return;
    }

    this.viewportObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.galleries.forEach((gallery) => gallery.setVisible(entry.isIntersecting));
      });
    }, { threshold: 0 });

    this.viewportObserver.observe(this);
  }

  /** Wires the dots to the track both ways: click scrolls, scroll updates. */
  initDots() {
    if (!this.track || this.dots.length < 2) return;

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const row = this.rows[i];
        if (!row) return;
        this.track.scrollTo({ left: row.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      });
    });

    if (!('IntersectionObserver' in window)) return;

    this.rowObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.setCurrentDot(this.rows.indexOf(entry.target));
      });
    }, { root: this.track, threshold: 0.6 });

    this.rows.forEach((row) => this.rowObserver.observe(row));
    this.setCurrentDot(0);
  }

  setCurrentDot(index) {
    if (index < 0) return;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }
}

customElements.define('plp-content-rows', PlpContentRows);
